/* eslint-disable complexity */
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureException } from '@sentry/node';

import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { POLICY_PAGE_LINK } from '~/src/constants';
import { BonusesPayment, PaymentFieldWrapper, PaymentSumInput } from '~/src/features/payments-abn/components/PaymentForms';
import { BonusesPayButton } from '~/src/features/payments-abn/components/PaymentForms/components/BonusesPayment/BonusesPayButton';
import { useSendBonusesPayment } from '~/src/features/payments-abn/components/PaymentForms/components/BonusesPayment/useSendBonusesPayment';
import ResultPopup, { defaultPopupState } from '~/src/features/payments-abn/components/PopupResult';
import { useSbpPayment } from '~/src/features/payments-abn/components/useSbpPayment';
import { useSendPayBonuses } from '~/src/features/payments-abn/hooks/useSendPayBonuses';
import { EValidityStatus, setPaymentResult, togglePayWithBonusesStatus } from '~/src/features/payments-abn/store/actionCreators';
import { TState } from '~/src/store';
import { getLoyaltyInfo } from '~/src/store/actions/loyalty';

import LoyaltyButton from '../LoyaltyButton';

import DesktopSbp from './desktopSbp';
import MobileSbp from './mobileSbp';

const SbpPaymentDetail = (): JSX.Element | null => {
    const dispatch = useDispatch();
    const {
        auth: { isAuth, clientPersonal },
        city: { provider: { providerId } },
        payment: { paymentData: { paymentSum, bonusesValue, isBonusesValueValid, isUseBonuses, paymentSumValidity, agreement, agreementEnc }, method, result },
        loyalty: { info: { bonuses } },
    } = useSelector((state: TState) => state);
    const { isOnlyBonusesPay, sendBonusesPayment } = useSendBonusesPayment();
    useSbpPayment();

    const { agreement: authAgreement } = clientPersonal || {};

    const isTablet = useMatchMedia(`(max-width: ${wideBreakpoints.tablet})`);

    const [isLoadingQrData, setIsLoadingQrData] = useState(false);
    const [popupState, setPopupState] = useState(defaultPopupState);

    const onSendPay = useSendPayBonuses();

    const onSendPayHandler = useCallback(async() => {
        if (popupState.isOpen) {
            setPopupState(defaultPopupState);
        }
        setIsLoadingQrData(true);

        try {
            const resp = await onSendPay();

            dispatch(setPaymentResult({ ...result, isBonusesSuccess: resp?.isSuccess }));
            dispatch(getLoyaltyInfo());
            return resp?.isSuccess;
        } catch (error) {
            captureException(error);
        } finally {
            setIsLoadingQrData(false);
        }
    }, [dispatch, onSendPay, popupState.isOpen, result]);

    const onPayWithoutBonuses = useCallback((reqAction?: () => Promise<void>) => {
        if (popupState.isOpen) {
            setPopupState(defaultPopupState);
        }

        dispatch(togglePayWithBonusesStatus(false));

        reqAction && reqAction();
    }, [dispatch, popupState.isOpen]);

    const onPayHandler = useCallback(async(reqAction?: () => Promise<void>) => {
        if (isUseBonuses) {
            const sendPayResult = await onSendPayHandler();

            if (sendPayResult) {
                reqAction && reqAction();
            } else {
                setPopupState({
                    isOpen: true,
                    title: 'Не удалось списать бонусы',
                    actions: <>
                        <BaseButton
                            type={btnTypes.ACCENT}
                            size={btnSizes.LARGE}
                            text={'Попробовать ещё раз'}
                            onClick={onSendPayHandler}
                        />
                        <BaseButton
                            type={btnTypes.SECONDARY}
                            size={btnSizes.LARGE}
                            text={'Оплатить без бонусов'}
                            onClick={() => onPayWithoutBonuses(reqAction)}
                        />
                    </>,
                });
            }
        } else {
            onPayWithoutBonuses(reqAction);
        }
    }, [onPayWithoutBonuses, onSendPayHandler, isUseBonuses]);

    const isShowLoyaltyButton = useMemo(() => {
        return isAuth && isUseBonuses && paymentSum > 0 && bonusesValue < bonuses && paymentSum === bonusesValue;
    }, [bonuses, isAuth, isUseBonuses, bonusesValue, paymentSum]);

    const canPayWithBonuses = useMemo(() => {
        const maxBonusPayment = paymentSum / 0.2;
        return !!paymentSum && bonusesValue && bonusesValue <= maxBonusPayment;
    }, [bonusesValue, paymentSum]);

    const hasAgreement = authAgreement || agreementEnc || agreement;
    const isBonusOk = isUseBonuses ? isBonusesValueValid : true;

    const commonProps = {
        providerId,
        userAgreement: `${agreementEnc || agreement || authAgreement}`,
        isDisabled: !isBonusOk || !paymentSum || paymentSumValidity.isValid !== EValidityStatus.IS_VALID || !hasAgreement,
        isLoadingQrData,
        onPayHandler,
        setIsLoadingQrData,
    };

    if (!(method?.methodType === 'sbp')) {
        return null;
    }

    return (
        <FullWidthListContainer $direction='column' $desktop={24} $mobile={16}>
            <H3 $withIndent={false}>Детали оплаты</H3>

            <PaymentFieldWrapper>
                <PaymentSumInput />

                <BonusesPayment />
            </PaymentFieldWrapper>
            <FullWidthListContainer $direction='column' $size={8}>
                {!isAuth &&
                    <PolicyNote
                        data-test='pay-agreement'
                        buttonText='Показать QR-код'
                        agreeUrl={POLICY_PAGE_LINK}
                    />
                }
                {isShowLoyaltyButton
                    ? <LoyaltyButton
                        isLoading={isLoadingQrData}
                        onPayHandler={onPayHandler}
                    />
                    : !isOnlyBonusesPay && (
                        isTablet
                            ? <MobileSbp {...commonProps} data-test='sbp-mobile' />
                            : <DesktopSbp {...commonProps} data-test='sbp-desktop' />
                    )
                }
                {isOnlyBonusesPay && (
                    <ButtonGroup>
                        <BonusesPayButton
                            btnText={'Оплатить бонусами'}
                            isDisable={!canPayWithBonuses}
                            onClick={sendBonusesPayment}
                        />
                    </ButtonGroup>
                )}
            </FullWidthListContainer>
            <ResultPopup {...popupState} onClose={() => setPopupState(defaultPopupState)} />
        </FullWidthListContainer>
    );
};

export default SbpPaymentDetail;
